/* eslint-disable no-param-reassign */
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { CreditPackSub, EmployerCreditTransactionSub } from 'src/openapi';
import type { EmployerCreditState } from '../types/employerCredit';
import {
  activateFreeUnlockOffer,
  claimFreeUnlockOfferLink,
  fetchCreditBalance,
  fetchCreditHistory,
  fetchCreditPacks,
  fetchUnlockedCandidates,
  unlockApplicant,
} from '../actions/employerCredit';
import {
  requestBulkAnalysis,
  unlockApplicantAiScores,
} from '../actions/applicantReview';
import { unlockDbAccess } from '../actions/inviteApplicant';

const initialState: EmployerCreditState = {
  balance: 0,
  unlockCost: 100,
  freeUnlockPromo: null,
  isActivatingFreeUnlockOffer: false,
  isFetchingBalance: false,
  packs: [],
  isFetchingPacks: false,
  history: [],
  isFetchingHistory: false,
  unlockedCandidates: [],
  isFetchingPipeline: false,
  pipelinePage: 1,
  pipelineTotalPages: 1,
  unlockingApplicantIds: [],
  isBuyCreditsOpen: false,
  isFreeUnlockOfferDialogOpen: false,
};

const employerCreditSlice = createSlice({
  name: 'employerCredit',
  initialState,
  reducers: {
    setBuyCreditsOpen: (state, action: PayloadAction<boolean>) => {
      state.isBuyCreditsOpen = action.payload;
    },
    // Lives in the store because two surfaces open the activation popup: the
    // layout (auto, on arriving through the invite link) and the dashboard banner.
    setFreeUnlockOfferDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isFreeUnlockOfferDialogOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCreditBalance.pending, (state) => {
        state.isFetchingBalance = true;
      })
      .addCase(fetchCreditBalance.fulfilled, (state, action) => {
        state.isFetchingBalance = false;
        state.balance = action.payload.balance ?? 0;
        state.unlockCost = action.payload.unlock_cost ?? state.unlockCost;
        state.freeUnlockPromo = action.payload.promo ?? null;
      })
      .addCase(fetchCreditBalance.rejected, (state) => {
        state.isFetchingBalance = false;
      })
      .addCase(activateFreeUnlockOffer.pending, (state) => {
        state.isActivatingFreeUnlockOffer = true;
      })
      .addCase(activateFreeUnlockOffer.fulfilled, (state, action) => {
        state.isActivatingFreeUnlockOffer = false;
        state.freeUnlockPromo = action.payload.promo ?? state.freeUnlockPromo;
      })
      .addCase(activateFreeUnlockOffer.rejected, (state) => {
        state.isActivatingFreeUnlockOffer = false;
      })
      .addCase(claimFreeUnlockOfferLink.fulfilled, (state, action) => {
        state.freeUnlockPromo = action.payload.promo ?? state.freeUnlockPromo;
      })
      .addCase(fetchCreditPacks.pending, (state) => {
        state.isFetchingPacks = true;
      })
      .addCase(fetchCreditPacks.fulfilled, (state, action) => {
        state.isFetchingPacks = false;
        state.packs = action.payload as CreditPackSub[];
      })
      .addCase(fetchCreditPacks.rejected, (state) => {
        state.isFetchingPacks = false;
      })
      .addCase(fetchCreditHistory.pending, (state) => {
        state.isFetchingHistory = true;
      })
      .addCase(fetchCreditHistory.fulfilled, (state, action) => {
        state.isFetchingHistory = false;
        state.history = action.payload as EmployerCreditTransactionSub[];
      })
      .addCase(fetchCreditHistory.rejected, (state) => {
        state.isFetchingHistory = false;
      })
      .addCase(fetchUnlockedCandidates.pending, (state) => {
        state.isFetchingPipeline = true;
      })
      .addCase(fetchUnlockedCandidates.fulfilled, (state, action) => {
        state.isFetchingPipeline = false;
        state.unlockedCandidates = action.payload.data ?? [];
        state.pipelinePage = action.payload.current_page ?? 1;
        state.pipelineTotalPages = action.payload.total_pages ?? 1;
      })
      .addCase(fetchUnlockedCandidates.rejected, (state) => {
        state.isFetchingPipeline = false;
      })
      .addCase(unlockApplicant.pending, (state, action) => {
        state.unlockingApplicantIds.push(action.meta.arg.applicantId);
      })
      .addCase(unlockApplicant.fulfilled, (state, action) => {
        state.unlockingApplicantIds = state.unlockingApplicantIds.filter(
          (id) => id !== action.meta.arg.applicantId,
        );
        if (action.payload.credits_balance != null) {
          state.balance = action.payload.credits_balance;
        }
      })
      .addCase(unlockApplicant.rejected, (state, action) => {
        state.unlockingApplicantIds = state.unlockingApplicantIds.filter(
          (id) => id !== action.meta.arg.applicantId,
        );
        if (
          (action.payload as { error?: string } | undefined)?.error ===
          'insufficient_employer_credits'
        ) {
          state.isBuyCreditsOpen = true;
        }
      })
      .addCase(requestBulkAnalysis.fulfilled, (state, action) => {
        if (action.payload.credits_balance != null) {
          state.balance = action.payload.credits_balance;
        }
      })
      .addCase(requestBulkAnalysis.rejected, (state, action) => {
        if (
          (action.payload as { error?: string } | undefined)?.error ===
          'insufficient_employer_credits'
        ) {
          state.isBuyCreditsOpen = true;
        }
      })
      .addCase(unlockApplicantAiScores.fulfilled, (state, action) => {
        if (action.payload.credits_balance != null) {
          state.balance = action.payload.credits_balance;
        }
      })
      .addCase(unlockApplicantAiScores.rejected, (state, action) => {
        if (
          (action.payload as { error?: string } | undefined)?.error ===
          'insufficient_employer_credits'
        ) {
          state.isBuyCreditsOpen = true;
        }
      })
      .addCase(unlockDbAccess.fulfilled, (state, action) => {
        if (action.payload.credits_balance != null) {
          state.balance = action.payload.credits_balance;
        }
      })
      .addCase(unlockDbAccess.rejected, (state, action) => {
        if (
          (action.payload as { error?: string } | undefined)?.error ===
          'insufficient_employer_credits'
        ) {
          state.isBuyCreditsOpen = true;
        }
      });
  },
});

export const { setBuyCreditsOpen, setFreeUnlockOfferDialogOpen } =
  employerCreditSlice.actions;
export default employerCreditSlice.reducer;
